import { CheckIcon } from "@heroicons/react/20/solid";
import { transportSedelStatusLabel } from "components/TransportSedelStatusTag";
import { cn } from "helpers/classNames";
import { TransportSedelStatus } from "types/transportSedel.type";

const STEPS = [
  TransportSedelStatus.Created,
  TransportSedelStatus.LoadedOnTruck,
  TransportSedelStatus.Completed,
];

// Samma statusfärger som TransportSedelStatusTag
const STEP_STYLES: Record<
  TransportSedelStatus,
  { circle: string; ring: string; line: string; label: string }
> = {
  [TransportSedelStatus.Created]: {
    circle: "bg-gray-500",
    ring: "ring-gray-300",
    line: "bg-gray-500",
    label: "text-gray-700",
  },
  [TransportSedelStatus.LoadedOnTruck]: {
    circle: "bg-pink-500",
    ring: "ring-pink-200",
    line: "bg-pink-500",
    label: "text-pink-700",
  },
  [TransportSedelStatus.Completed]: {
    circle: "bg-cyan-600",
    ring: "ring-cyan-200",
    line: "bg-cyan-600",
    label: "text-cyan-800",
  },
};

type Props = {
  status: TransportSedelStatus;
  className?: string;
  /** Status vars steg ska "poppa" med en ring – t.ex. nyss lastad */
  celebrateStatus?: TransportSedelStatus | null;
};

export const OperatorStatusStepper = ({
  status,
  className,
  celebrateStatus,
}: Props) => {
  const idx = STEPS.indexOf(status);
  if (idx === -1) {
    console.warn(
      `OperatorStatusStepper: okänd TransportSedelStatus "${status}", faller tillbaka till första steget`,
    );
  }
  // Klampa okända statusar till första steget så att steppern inte
  // tyst renderar alla steg som onådda
  const currentIndex = idx === -1 ? 0 : idx;
  return (
    <ol className={cn("flex w-full max-w-xs", className)}>
      {STEPS.map((step, i) => {
        const isReached = i <= currentIndex;
        const isCurrent = i === currentIndex;
        const isCelebrating = step === celebrateStatus;
        const style = STEP_STYLES[step];
        return (
          <li key={step} className="relative flex flex-1 flex-col items-center">
            {i > 0 && (
              <div
                className={cn(
                  "absolute right-1/2 top-3.5 h-0.5 w-full",
                  isReached ? style.line : "bg-gray-100",
                )}
                aria-hidden="true"
              />
            )}
            <span
              className={cn(
                "relative z-10 flex h-7 w-7 items-center justify-center rounded-full",
                isReached ? style.circle : "border-2 border-gray-200 bg-white",
                isCurrent && cn("ring-2 ring-offset-2", style.ring),
                isCelebrating && "operator-step-pop",
              )}
            >
              {isCelebrating && (
                <span
                  className={cn(
                    "absolute inset-0 animate-ping rounded-full opacity-75",
                    style.circle,
                  )}
                  aria-hidden="true"
                />
              )}
              {isReached ? (
                <CheckIcon className="h-4 w-4 text-white" aria-hidden="true" />
              ) : (
                <span className="h-2 w-2 rounded-full bg-gray-200" />
              )}
            </span>
            <span
              className={cn(
                "mt-2 text-[11px] leading-none",
                isReached ? style.label : "text-gray-400",
                isCurrent ? "font-bold" : "font-medium",
              )}
            >
              {transportSedelStatusLabel(step)}
            </span>
          </li>
        );
      })}
    </ol>
  );
};
